import React, { useState } from "react";
import { X, Wallet, LogOut, ChevronRight, Check, ExternalLink } from "lucide-react";
import { useAppKit, useAppKitAccount, useDisconnect } from "@reown/appkit/react";
import { useCurrentAccount, useDAppKit, useWallets as useSuiWallets } from "@mysten/dapp-kit-react";
import { useTronWallet } from "@/lib/TronProviders";
import { useBitcoinWallet } from "@/lib/BitcoinProviders";
import { toast } from "sonner";

const short = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : "");

function Section({ title, subtitle, connected, onDisconnect, children }) {
  return (
    <div className="border-t border-[var(--border)] first:border-t-0 px-4 py-3">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-[var(--text-subtle)] font-bold">{title}</div>
          {subtitle && <div className="text-[11px] text-[var(--text-muted)] mt-0.5">{subtitle}</div>}
        </div>
        {connected && (
          <button onClick={onDisconnect} className="text-[11px] text-[var(--text-muted)] hover:text-[var(--down)] flex items-center gap-1">
            <LogOut size={11} /> disconnect
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function Tile({ icon, name, connected, address, onClick, disabled, iconNode }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all ${
        connected ? "bg-[var(--accent-soft)] border border-[var(--border-accent)]" : "hover:bg-[var(--panel-2)] border border-transparent hover:border-[var(--border)]"
      } disabled:opacity-40`}
      data-testid={`wallet-tile-${(name || "").toLowerCase().replace(/\s+/g, "-")}`}
    >
      {iconNode ? (
        iconNode
      ) : icon ? (
        <img src={icon} alt="" className="w-8 h-8 rounded-lg bg-[var(--panel-2)] flex-shrink-0" onError={(e) => (e.currentTarget.style.visibility = "hidden")} />
      ) : (
        <span className="w-8 h-8 rounded-lg bg-[var(--panel-2)] flex items-center justify-center flex-shrink-0">
          <Wallet size={14} className="text-[var(--text-muted)]" />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] text-[var(--text)] font-semibold truncate">{name}</div>
        {connected && address && (
          <div className="text-[11px] font-mono text-[var(--accent)] truncate">{short(address)}</div>
        )}
      </div>
      {connected ? <Check size={15} className="text-[var(--accent)] flex-shrink-0" /> : <ChevronRight size={15} className="text-[var(--text-muted)] flex-shrink-0" />}
    </button>
  );
}

export default function WalletHub({ open, onClose }) {
  // Unified modal handles EVM + Solana + Bitcoin
  const { open: openAppKit } = useAppKit();
  const evmAcc = useAppKitAccount({ namespace: "eip155" });
  const solAcc = useAppKitAccount({ namespace: "solana" });
  const btcAcc = useAppKitAccount({ namespace: "bip122" });
  const { disconnect } = useDisconnect();

  // Sui
  const suiAcc = useCurrentAccount();
  const suiConnected = !!suiAcc?.address;
  const suiWallets = useSuiWallets();
  const suiDApp = useDAppKit();

  // Tron & Bitcoin (kept as-is; Reown's Bitcoin coverage is limited to a few wallets)
  const tron = useTronWallet();
  const btcInj = useBitcoinWallet();

  const [busy, setBusy] = useState(null);

  const totalConnected = [
    evmAcc.isConnected, solAcc.isConnected, btcAcc.isConnected,
    suiConnected, tron.connected, btcInj.connected,
  ].filter(Boolean).length;

  if (!open) return null;

  const doOpenReown = (namespace) => {
    onClose();
    // Small delay so our modal unmounts before Reown mounts, prevents z-index race.
    setTimeout(() => openAppKit(namespace ? { namespace } : undefined), 60);
  };
  const doSui = async (w) => {
    setBusy(`sui-${w.name}`);
    try { await suiDApp.connectWallet({ wallet: w }); }
    catch (e) { toast.error(e.message || "Connection failed"); }
    finally { setBusy(null); }
  };
  const doTron = async () => {
    setBusy("tron");
    try { await tron.connect(); }
    catch (e) { toast.error(e.message || "Connection failed"); }
    finally { setBusy(null); }
  };
  const doBtcInjected = async (kind) => {
    setBusy(`btc-${kind}`);
    try { await btcInj.connect(kind); }
    catch (e) { toast.error(e.message || "Connection failed"); }
    finally { setBusy(null); }
  };

  const UnifiedIcon = (
    <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#4F46E5] to-[#7C3AED] flex items-center justify-center flex-shrink-0 shadow-[0_4px_12px_-2px_rgba(79,70,229,0.45)]">
      <Wallet size={16} className="text-white" />
    </span>
  );

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-[rgba(15,23,42,0.35)] backdrop-blur-md" />
      <div className="relative panel w-full max-w-md overflow-hidden max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="h-14 px-5 border-b border-[var(--border)] flex items-center justify-between">
          <div>
            <div className="text-[14px] text-[var(--text)] font-bold">Connect wallet</div>
            <div className="text-[11px] text-[var(--text-muted)]">
              {totalConnected > 0 ? `${totalConnected} wallet${totalConnected>1?"s":""} connected` : "One unified modal for every chain"}
            </div>
          </div>
          <button className="w-9 h-9 rounded-lg text-[var(--text-muted)] hover:text-[var(--text)] hover:bg-[var(--panel-2)] flex items-center justify-center" onClick={onClose} data-testid="wallet-hub-close">
            <X size={15} />
          </button>
        </div>

        <div className="overflow-y-auto">
          <Section
            title="EVM · Solana · Bitcoin"
            subtitle="300+ wallets · WalletConnect · MetaMask · Phantom · Xverse"
            connected={evmAcc.isConnected || solAcc.isConnected || btcAcc.isConnected}
            onDisconnect={() => disconnect()}
          >
            {evmAcc.isConnected && (
              <div className="text-[11px] font-mono text-[var(--text-muted)] mb-2 pl-1">
                EVM · <span className="text-[var(--accent)]">{short(evmAcc.address)}</span>
              </div>
            )}
            {solAcc.isConnected && (
              <div className="text-[11px] font-mono text-[var(--text-muted)] mb-2 pl-1">
                Solana · <span className="text-[var(--accent)]">{short(solAcc.address)}</span>
              </div>
            )}
            {btcAcc.isConnected && (
              <div className="text-[11px] font-mono text-[var(--text-muted)] mb-2 pl-1">
                Bitcoin · <span className="text-[var(--accent)]">{short(btcAcc.address)}</span>
              </div>
            )}
            <Tile
              iconNode={UnifiedIcon}
              name="Open unified wallet modal"
              connected={false}
              onClick={() => doOpenReown()}
              disabled={busy != null}
            />
          </Section>

          <Section title="Sui" connected={suiConnected} onDisconnect={() => suiDApp.disconnectWallet?.()}>
            <div className="grid grid-cols-1 gap-1">
              {(suiWallets || []).length === 0 && (
                <div className="text-[11px] text-[var(--text-muted)] p-2 font-mono">
                  No Sui wallet detected — install{" "}
                  <a href="https://suiwallet.com/" target="_blank" rel="noopener noreferrer" className="text-[var(--accent)] hover:underline inline-flex items-center gap-0.5">Sui Wallet <ExternalLink size={9} /></a>{" "}
                  or{" "}
                  <a href="https://suiet.app/" target="_blank" rel="noopener noreferrer" className="text-[var(--accent)] hover:underline inline-flex items-center gap-0.5">Suiet <ExternalLink size={9} /></a>.
                </div>
              )}
              {(suiWallets || []).map((w) => (
                <Tile
                  key={w.name}
                  icon={w.icon}
                  name={w.name}
                  connected={suiConnected}
                  address={suiAcc?.address}
                  onClick={() => doSui(w)}
                  disabled={busy != null}
                />
              ))}
            </div>
          </Section>

          <Section title="Tron" connected={tron.connected} onDisconnect={tron.disconnect}>
            <Tile
              icon="https://cryptologos.cc/logos/tron-trx-logo.png"
              name="TronLink / OKX"
              connected={tron.connected}
              address={tron.address}
              onClick={doTron}
              disabled={busy != null}
            />
          </Section>

          <Section title="Bitcoin (native providers)" connected={btcInj.connected} onDisconnect={btcInj.disconnect}>
            <div className="grid grid-cols-1 gap-1">
              <Tile
                icon="https://next-cdn.unisat.io/_/285/logo/color.svg"
                name="UniSat"
                connected={btcInj.connected && btcInj.provider === "unisat"}
                address={btcInj.address}
                onClick={() => doBtcInjected("unisat")}
                disabled={busy != null}
              />
              <Tile
                icon="https://www.xverse.app/favicon-32x32.png"
                name="Xverse"
                connected={btcInj.connected && btcInj.provider === "xverse"}
                address={btcInj.address}
                onClick={() => doBtcInjected("xverse")}
                disabled={busy != null}
              />
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}
